// src/components/chat/DocumentCitationBubble.tsx

/**
 * DocumentCitationBubble Component
 *
 * Displays a document citation as an inline chip showing a file icon + truncated document name.
 * Gray/neutral color scheme, rounded rectangle shape.
 *
 * Features:
 * - File icon + truncated document name
 * - Hover dispatches to global tooltip context (survives streaming remounts)
 * - Gray/neutral color scheme (#F5F5F5 background)
 * - With `groupRefs`, stands for several adjacent document citations: the first
 * one's name plus "+N", a paged tooltip, pinned open by click/tap/Enter
 */

import React, { useRef, useCallback } from "react";
import { useDocumentCitations } from "../../contexts/DocumentCitationContext";
import type { DocumentCitationTooltipData } from "../../contexts/DocumentCitationTooltipContext";
import { useDocumentCitationTooltip } from "../../contexts/DocumentCitationTooltipContext";
import type { DocumentCitation } from "../../interfaces/documentCitationTypes";
import { isCatylexCitation, isPlaybookCitation } from "../../interfaces/documentCitationTypes";
import { createLogger } from "../../utils/logger";
import { CitationCountBadge } from "./CitationGroupControls";
import { getDocumentTheme } from "./DocumentCitationIcons";
import { useCitationGroupChip } from "./hooks/useCitationGroupChip";

const log = createLogger("DocCitationBubble");

export interface DocumentCitationBubbleProps {
  /** Reference matching the <doc-cite data-ref="REF" /> tag */
  citationRef: string;
  /** Refs of a merged run of document citations, `citationRef` first. Absent for a single citation. */
  groupRefs?: string[];
}

/**
 * Format citation location info based on citation type.
 *
 * block_location: the label of the cited unit, as the BE resolved it
 * ("Page 3", a sheet name, a clause number), or nothing when the unit has
 * no label.
 *
 * search_result_location citations are routed to their tool-specific bubble
 * (playbook / catylex) before reaching here.
 */
function formatLocation(citation: DocumentCitation): string {
  switch (citation.citation_type) {
    case "block_location":
      return citation.location_label ?? "";
    default:
      return "";
  }
}

/** Icon size inside the chip; the tooltip header uses the icons' own default. */
const CHIP_ICON_SIZE = 14;

const toTooltipData = (citation: DocumentCitation): DocumentCitationTooltipData => ({
  documentTitle: citation.document_title,
  citedText: citation.cited_text,
  location: formatLocation(citation),
});

export const DocumentCitationBubble: React.FC<DocumentCitationBubbleProps> = ({
  citationRef,
  groupRefs,
}) => {
  const { lookupCitation } = useDocumentCitations();
  const { state, showTooltip, hideTooltip, showTooltipGroup, pageTooltip, closeTooltip } =
    useDocumentCitationTooltip();
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const citation = lookupCitation(citationRef);
  const groupCitations = (groupRefs ?? [])
    .map(lookupCitation)
    .filter((c): c is DocumentCitation => !!c && !isPlaybookCitation(c) && !isCatylexCitation(c));
  const isGroup = groupCitations.length > 1;
  const isOpen = state.isVisible && !!state.group && state.group.anchorEl === bubbleRef.current;

  const openGroup = useCallback(
    (pinned: boolean) => {
      if (!bubbleRef.current) return;
      showTooltipGroup(bubbleRef.current, groupCitations.map(toTooltipData), pinned);
    },
    [showTooltipGroup, groupCitations]
  );

  const groupChipProps = useCitationGroupChip({
    isGroup,
    isOpen,
    isPinned: state.pinned,
    open: openGroup,
    close: closeTooltip,
    page: pageTooltip,
  });

  const handleMouseEnter = useCallback(() => {
    if (isGroup) {
      openGroup(false);

      return;
    }
    const rect = bubbleRef.current?.getBoundingClientRect() ?? null;
    const cit = lookupCitation(citationRef);
    log.debug("Hover on citation pill", {
      citationRef,
      hasData: !!cit,
      documentTitle: cit?.document_title,
    });
    if (cit) {
      showTooltip({
        anchorRect: rect,
        documentTitle: cit.document_title,
        citedText: cit.cited_text,
        location: formatLocation(cit),
      });
    }
  }, [isGroup, openGroup, showTooltip, lookupCitation, citationRef]);

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  // No payload for this ref (not streamed yet, or never sent): render nothing
  // rather than invent a source label. The context update re-renders the chip.
  if (!citation) return null;
  const documentTitle = citation.document_title;
  const { Icon } = getDocumentTheme(documentTitle);
  const ariaLabel = isGroup
    ? `${groupCitations.length} citations: ${groupCitations.map((c) => c.document_title).join(", ")}`
    : `Citation from ${documentTitle}`;

  return (
    <span
      ref={bubbleRef}
      className="doc-citation-bubble"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...groupChipProps}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "2px 8px",
        margin: "0 2px",
        backgroundColor: "#F5F5F5",
        border: "1px solid #E0E0E0",
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: 500,
        color: "#424242",
        cursor: "pointer",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "#EEEEEE";
        e.currentTarget.style.borderColor = "#90CAF9";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "#F5F5F5";
        e.currentTarget.style.borderColor = "#E0E0E0";
      }}
    >
      <Icon size={CHIP_ICON_SIZE} />
      <span
        style={{
          maxWidth: "160px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {documentTitle}
      </span>
      {isGroup && <CitationCountBadge count={groupCitations.length - 1} />}
    </span>
  );
};

export default DocumentCitationBubble;
